// Billing B2.1a: a phone input that shows "(   )    -    " and fills it as the
// booker types. Other countries get their own groups, from Google's rules
// (libphonenumber-js), loaded only when a booker types one.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { formatPhone } from '../../helper/phoneMask';
import { colors, Input } from '../styles';

const Wrapper = styled.div`
  position: relative;
`;

// Same font as the ghost, and equal-width digits, so ')' and '-' never move.
const TelInput = styled(Input)`
  font-family: inherit;
  font-variant-numeric: tabular-nums;
  background: transparent;
`;

// Drawn over the input, at the same place. The typed part is invisible, so
// only the rest of the pattern shows. Screen readers and taps skip it.
const Ghost = styled.div`
  position: absolute;
  inset: 0;
  font-size: 14px;
  line-height: 1;
  margin-top: 6px;
  font-variant-numeric: tabular-nums;
  white-space: pre;
  overflow: hidden;
  pointer-events: none;
  color: ${colors.placeholderText};

  span {
    visibility: hidden;
  }
`;

// '(000) 000-0000' → empty digit slots, visible symbols.
const ghostParts = (ghost) => ghost.split(/(\d+)/).map((part, i) => (/\d/.test(part) ? <span key={i}>{part}</span> : part));

const asYouType =
  ({ AsYouType }) =>
  (text) =>
    new AsYouType().input(text);

let intlFormatPromise;
const loadIntlFormat = () => {
  intlFormatPromise =
    intlFormatPromise ||
    import('libphonenumber-js/min')
      .then(asYouType)
      .catch((e) => {
        intlFormatPromise = null; // Try again at the next change.
        throw e;
      });
  return intlFormatPromise;
};

export default function PhoneInput({ value, onChange, ...props }) {
  const ref = useRef(null);
  const caret = useRef(null);
  const [intlFormat, setIntlFormat] = useState(null);
  // Always shown formatted, even before the other-country rules load. The
  // value itself is never rewritten from here: a late rewrite could undo a
  // key typed in the meantime.
  const shown = formatPhone(value, value.length, { intlFormat });

  // Load the other-country rules the first time a booker needs them.
  const needsIntl = shown.mode === 'intl' && !intlFormat;
  useEffect(() => {
    if (!needsIntl) return;
    let live = true;
    loadIntlFormat()
      .then((format) => live && setIntlFormat(() => format))
      .catch(() => {}); // Offline: the number stays as typed and still books.
    return () => {
      live = false;
    };
  }, [needsIntl, value]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && caret.current !== null && document.activeElement === el) el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const handleChange = (e) => {
    const el = e.target;
    const out = formatPhone(el.value, el.selectionStart ?? el.value.length, {
      prevText: shown.text,
      deleteForward: e.nativeEvent?.inputType === 'deleteContentForward',
      intlFormat
    });
    caret.current = out.caret;
    if (out.text === shown.text) {
      // No change for React to render: put the text and caret back here.
      el.value = out.text;
      el.setSelectionRange(out.caret, out.caret);
    }
    onChange(out.text);
  };

  return (
    <Wrapper>
      <TelInput ref={ref} type="tel" inputMode="tel" autoComplete="tel" {...props} value={shown.text} onChange={handleChange} />
      {shown.ghost && (
        <Ghost aria-hidden="true" data-testid="phone-ghost">
          <span>{shown.text}</span>
          {ghostParts(shown.ghost)}
        </Ghost>
      )}
    </Wrapper>
  );
}
